import React from 'react';
import Highcharts from 'highcharts';
import _merge from 'lodash/merge';
import HighchartsReact from 'highcharts-react-official';

import { ChartProps } from './types';
import * as DefaultChartConfig from './options';
import SizeableChartWrapper from './SizableChartWrapper';

function buildOptions <T> ({
  isPercentage,
  presentationFont,
  showChartLabels,
  dataSeries,
  options,
}: ChartProps<T>): Highcharts.Options {
  return _merge(({
    chart: DefaultChartConfig.createDefaultChartOptions(),
    title: DefaultChartConfig.createDefaultTitleOptions(),
    credits: DefaultChartConfig.createDefaultCreditsOptions(),
    exporting: DefaultChartConfig.createDefaultExportingOptions(),
    legend: DefaultChartConfig.createDefaultLegendOptions(),
    xAxis: DefaultChartConfig.createDefaultXAxisOptions(),
    yAxis: DefaultChartConfig.createDefaultYAxisOptions(presentationFont, isPercentage),
    plotOptions: DefaultChartConfig.createDefaultPlotOptions(showChartLabels, presentationFont),
    series: dataSeries || [],
    rangeSelector: DefaultChartConfig.createDefaultRangeSelectorOptions(),
    scrollbar: DefaultChartConfig.createDefaultScrollbarOptions(),
    tooltip: DefaultChartConfig.createDefaultTooltipOptions(),
  }), options || {});
}

export default function VeryNiceChart <OptionsType extends Highcharts.SeriesOptions> (
  props: ChartProps<OptionsType>,
): React.ReactElement {
  const { afterCreated } = props;
  const chartRef = React.useRef<{
    chart: Highcharts.Chart,
    container: React.RefObject<HTMLDivElement>
  }>();

  const onSize = React.useCallback(() => {
    if (chartRef.current && chartRef.current.chart) {
      chartRef.current.chart.reflow();
    }
  }, []);

  return (
    <SizeableChartWrapper onSize={onSize}>
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={buildOptions(props)}
        callback={afterCreated}
        containerProps={{ style: { height: '100%', width: '100%' } }}
      />
    </SizeableChartWrapper>
  );
}
